<template>
  <ActionButton
    class="w-auto"
    :class="getOrders().length == 0 ? 'disabled' : ''"
    @customEvent="onCheckout"
    :theme="{ primary, secondary, event, danger }"
    :disabled="isLoading"
    label=""
  >
    <Spinner v-if="isLoading" theme="currentColor" />
    <span v-else>Checkout</span>
  </ActionButton>
</template>

<script>
import query from "@/api/query";
import axios from "@/helpers/base";
import invoiceCode from "@/helpers/algo/invoiceCode";
import { mapGetters, mapActions } from "vuex";
import {
  bookingConfirmEmail,
  bookingConfirmNotifyOwner
} from "@/helpers/mailer";
import { socketSender } from "@/helpers/socket";
import Spinner from "@/components/Spinner";
import ActionButton from "@/components/ActionButton";

export default {
  name: "CheckoutButton",
  components: {
    Spinner,
    ActionButton
  },
  props: {
    user: {
      type: [Object],
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      proceedToCheckout: false,
      currentUser:
        this.$store.getters.getUser.firstName +
        " " +
        this.$store.getters.getUser.lastName,
      module: this.$route.matched[0].name,
      errorPackage: null
    };
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    }
  },
  methods: {
    ...mapGetters({
      getOrders: "getOrders",
      invalidOrder: "GET_INVALID_ORDER"
    }),
    ...mapActions({
      ORDER_CLEAR: "ORDER_CLEAR",
      ORDER_ADD_INVOICE_CODE: "ORDER_ADD_INVOICE_CODE"
    }),
    async onCheckout() {
      const orders = this.getOrders();
      const ordersCount = orders.length;
      this.isLoading = true;
      let dates = [];
      let products = [];

      if (orders.length < 1) {
        this.$toastr.warning("Please add item", "Warning");
        return;
      }

      orders.map(e => {
        products.push(e.Product.name);
        dates.push({
          start: e.OrderPackage.startAt,
          end: e.OrderPackage.endAt
        });
      });
      let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const checkProductsProms = Promise.all(
        orders.map(order =>
          axios
            .get(
              `/products/${order.Product.id}/availability?qp[start]=${order.OrderPackage.startAt}&qp[end]=${order.OrderPackage.endAt}&timeZone=${timeZone}`
            )
            .then(({ data }) => data)
        )
      );

      Promise.all([checkProductsProms])
        .then(results => {
          const products = results[0];
          this.proceedToCheckout = products.every(i => i.available); // Set to false if 1 or n items are not available.
          let orderIndex = orders.findIndex(
            (i, key) => products[key].schedule === false
          );
          this.$store.dispatch("SET_INVALID_ORDER", {
            index: orderIndex,
            data: {
              ...orders[orderIndex],
              ...{
                progress: "invalidSchedule"
              }
            }
          });
          if (!this.proceedToCheckout) {
            let blockedBy = "availability";
            if (!products.every(i => i.schedule)) {
              // Check if product schedule is available
              blockedBy = "schedule";
            }
            this.overlapNotif(blockedBy);
            return;
          }

          this.$swal({
            title: "Would you like to proceed payment?",
            text: `You have ${ordersCount} item(s) for checkout`,
            type: "info",
            showCancelButton: true,
            confirmButtonText: "Proceed",
            confirmButtonColor: this.primary,
            reverseButtons: true
          }).then(async result => {
            if (result.dismiss) {
              return;
            }

            this.$emit("fetching", true);

            const userId = this.user.id;
            const order = {
              userId: userId,
              code: "",
              dueAt: null,
              note: null,
              totalAmount: orders
                .map(
                  order =>
                    order.OrderPackage.total * order.OrderPackage.quantity
                )
                .reduce((accumulator, num) => accumulator + num),
              OrderPackages: orders.map(o => {
                const {
                  endAt,
                  startAt,
                  packageId,
                  total,
                  quantity
                } = o.OrderPackage;
                return {
                  endAt,
                  startAt,
                  packageId,
                  agenda: "",
                  amount: total * quantity,
                  quantity,
                  OrderPackageUser: []
                };
              }),
              products: orders.map(product => {
                const { id } = product.Product;
                return {
                  id
                };
              })
            };
            const saveOrder = await query.postData({
              model: "orders",
              data: order
            });

            if (saveOrder.data.data.status == "error") {
              this.isLoading = false;
              this.$emit("fetching", false);
              this.overlapNotif();
              return;
            }

            const {
              data: {
                data: { id, OrderPackages }
              }
            } = saveOrder;
            let code = invoiceCode(id);
            await this.ORDER_ADD_INVOICE_CODE(id);

            /**
             * Send Logs
             */
            let log_message = "";
            let systemModule;

            switch (this.$route.name) {
              case "admin-checkout":
                systemModule = "ADMIN";
                break;
              case "UserCheckout":
                systemModule = "USER";
                break;
            }
            if (systemModule == "USER") {
              log_message = `New unpaid (${code}) has been created by ${systemModule}: ${this.currentUser}`;
            } else {
              log_message = `New unpaid (${code}) for ${this.user.firstName} ${this.user.lastName} created on ${systemModule}: ${this.currentUser}`;
            }
            /**
             * SOCKET: new unpaid booking for log
             */
            await socketSender("newOrder", log_message);
            // TODO: Add needed data.
            await bookingConfirmEmail({
              to: this.user.email,
              fullName: this.user.firstName + " " + this.user.lastName,
              details: {
                invoiceId: id,
                code: code,
                order: OrderPackages,
                orders,
                products,
                dates
              }
            });
            // TODO: Add needed data.
            await bookingConfirmNotifyOwner({
              details: {
                invoiceId: id,
                code: code,
                userId: this.user.id,
                email: this.user.email,
                fullName: this.user.firstName + " " + this.user.lastName,
                orders
              }
            });
            this.$router.push({
              name: this.module + "-invoice",
              params: { userId: this.user.id, invoiceId: id }
            });
            await this.ORDER_CLEAR();
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    overlapNotif(blockedBy) {
      if (blockedBy === "schedule") {
        this.$swal({
          title: "Oops!",
          text:
            "The schedule for this product has been updated. Please adjust your booking accordingly.",
          type: "warning",
          confirmButtonText: "Update Booking"
        }).then(() => {
          this.$emit("onEditSchedule", this.invalidOrder());
        });
        return;
      }
      this.$swal(
        "Warning",
        "Overlapped booking, please make another booking",
        "warning"
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.btn {
  border-color: transparent;
  min-width: 110px;
}

.disabled {
  cursor: not-allowed !important;
}
</style>
